import type { MeUser } from '@tomato/shared';
import { create } from 'zustand';

import { api, ApiError } from '../api/client';

interface MeResponse {
  user: MeUser;
  onboarded: boolean;
}

type Status = 'loading' | 'anonymous' | 'authenticated';

interface SessionState {
  me: MeUser | null;
  onboarded: boolean;
  status: Status;
  /** 방금 가입(온보딩)을 마쳤는가 — 환영 화면(가입 후 넛지)을 1회 띄우는 데 쓴다. */
  justOnboarded: boolean;
  /** 앱 시작 시 1회. 쿠키가 살아 있으면 세션을 복구한다. */
  load: () => Promise<void>;
  setSession: (res: MeResponse) => void;
  /** 온보딩 완료 — 환영 화면을 띄운다. */
  completeOnboarding: (res: MeResponse) => void;
  dismissWelcome: () => void;
  /** 프로필 수정 등으로 내 정보만 갱신할 때. */
  updateMe: (user: MeUser) => void;
  /** 서버에서 내 정보를 다시 받아 온다(예: 글·댓글 작성 후 토마토 잔액 반영). */
  refreshMe: () => Promise<void>;
  logout: () => Promise<void>;
}

export const useSessionStore = create<SessionState>((set) => ({
  me: null,
  onboarded: false,
  status: 'loading',
  justOnboarded: false,

  load: async () => {
    try {
      const res = await api.get<MeResponse>('/api/me');
      set({ me: res.user, onboarded: res.onboarded, status: 'authenticated' });
    } catch (err) {
      // 401은 정상적인 '로그인 안 한 상태'다.
      if (!(err instanceof ApiError) || err.status !== 401) {
        console.error('[session] 복구 실패', err);
      }
      set({ me: null, onboarded: false, status: 'anonymous' });
    }
  },

  setSession: (res) => set({ me: res.user, onboarded: res.onboarded, status: 'authenticated' }),

  completeOnboarding: (res) =>
    set({ me: res.user, onboarded: res.onboarded, status: 'authenticated', justOnboarded: true }),

  dismissWelcome: () => set({ justOnboarded: false }),

  updateMe: (user) => set({ me: user }),

  refreshMe: async () => {
    try {
      const res = await api.get<MeResponse>('/api/me');
      set({ me: res.user, onboarded: res.onboarded });
    } catch {
      /* 잔액 갱신 실패는 조용히 — 다음 로드 때 맞춰진다. */
    }
  },

  logout: async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      set({ me: null, onboarded: false, status: 'anonymous' });
    }
  },
}));
